import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import { listExams, exportCsv, exportPdf } from '../../api/admin.api';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const StatusBadge = ({ published }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
    published ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${published ? 'bg-emerald-500' : 'bg-slate-400'}`} />
    {published ? 'Published' : 'Draft'}
  </span>
);

const Reports = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    listExams()
      .then((res) => setExams(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  const handleCsv = async (examId) => {
    setDownloading((d) => ({ ...d, [`csv_${examId}`]: true }));
    try {
      const res = await exportCsv(examId);
      downloadBlob(res.data, `results-exam-${examId}.csv`);
    } finally {
      setDownloading((d) => ({ ...d, [`csv_${examId}`]: false }));
    }
  };

  const handlePdf = async (examId) => {
    setDownloading((d) => ({ ...d, [`pdf_${examId}`]: true }));
    try {
      const res = await exportPdf(examId);
      downloadBlob(res.data, `results-exam-${examId}.pdf`);
    } finally {
      setDownloading((d) => ({ ...d, [`pdf_${examId}`]: false }));
    }
  };

  return (
    <AdminLayout>
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <p className="text-slate-500 text-sm mt-0.5">Download results and view candidate performance per exam.</p>
      </div>

      {loading ? (
        <Spinner className="py-20" />
      ) : exams.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-3">📈</div>
          <h3 className="text-slate-700 font-semibold mb-1">No exams found</h3>
          <p className="text-slate-400 text-sm">Create an exam first to see reports here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Exam</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Duration</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Questions</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {exams.map((exam) => (
                <tr key={exam.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-semibold text-slate-800">{exam.title}</p>
                    {exam.description && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{exam.description}</p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {exam.duration_minutes} min
                    </span>
                  </td>
                  <td className="px-5 py-4 text-slate-500">{exam.question_count ?? 0}</td>
                  <td className="px-5 py-4"><StatusBadge published={exam.is_published} /></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/admin/exams/${exam.id}/results`)}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Results
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={downloading[`csv_${exam.id}`]}
                        onClick={() => handleCsv(exam.id)}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        CSV
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={downloading[`pdf_${exam.id}`]}
                        onClick={() => handlePdf(exam.id)}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export default Reports;
